/* eslint-disable no-console */
"use client"

import type { RecoverResponse } from "./recover/useIP"
import { isBrowser } from "@local/utils/src/isBrowser"
import { APP_URL } from "@local/utils/src/services"
import Cookies from "js-cookie"
import { create } from "zustand"
import { getTrackingCookies } from "./getTrackingCookies"
import { cleanDomain } from "@local/utils/src/cleanDomain"
import { sendBacon } from "@local/utils/src/sendBeacon"
import { objectToShiftedBase64 } from "@local/utils/src/safeBase64"
import { getRecoveryStorage } from "./recover/getRecoveryStorage"
import { getBrowserLanguageProps } from "./getBrowserLanguage"
import { trackPostHog } from "./scripts/posthog"
import { v7 as uuidv7 } from "uuid"
import { track } from "@vercel/analytics"
import {
  dispatchGtag,
  dispatchExponea,
  dispatchDataLayerPush,
} from "./dispatch"
// [VV12-TEARDOWN] lp-scrollytelling A/B test
import {
  isVv12Variant,
  VV12_COOKIE,
  VV12_FEATURE_FLAG,
} from "@local/tracking/src/experiments/scrollytelling"
// [/VV12-TEARDOWN]
// [VV11-TEARDOWN] lp-no-discount-framing A/B test (DEV-10019)
import {
  VV11_COOKIE,
  VV11_FEATURE_FLAG,
  isVv11Variant,
} from "@local/tracking/src/experiments/noDiscountFraming"
// [/VV11-TEARDOWN]
// [VV9-TEARDOWN] all-paypal-express A/B test
import {
  VV9_COOKIE,
  VV9_FEATURE_FLAG,
  isVv9Variant,
} from "@local/tracking/src/experiments/paypalVisibility"
// [/VV9-TEARDOWN]

// https://developers.google.com/tag-platform/tag-manager/web/datalayer
// https://developers.google.com/analytics/devguides/collection/ua/gtm/enhanced-ecommerce#data-layer

export const baseParametersForTracking = () => {
  if (!isBrowser()) {
    return null
  }

  const rData: RecoverResponse | undefined = window.$r
  const today = new Date()
  const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ]

  const { ga, gtmUpi, vibes, exponeaId } = getTrackingCookies()
  const { bot, ip, loc, geo, ua, weather } = getRecoveryStorage()

  const country = Cookies.get("country")
  const currency = Cookies.get("div-currency")
  const locale = Cookies.get("div-lang")

  const google_analytics = ga
    ? ga.split(".").slice(2, 3).join() + "." + ga.split(".").slice(3, 4).join()
    : ""

  const pageType = window.location.pathname.includes("/lp/") ? "anchor" : "mini"

  return {
    approx_zip_code: geo?.postalCode ?? loc?.zip ?? rData?.loc?.zip,
    browser_name: ua?.browser.name ?? rData?.ua?.browser.name,
    ...getBrowserLanguageProps(),
    browser_type: ua?.device.type ?? rData?.ua?.device.type ?? "desktop",
    checkout_language: locale,
    checkout_locale: country,
    city: geo?.city ?? loc?.city ?? rData?.loc?.city,
    connection_type: loc ? (loc.mobile ? "cellular" : "wifi") : "unknown",
    cookie: exponeaId ?? window?.$cdata?.id?.exponea_id,
    country: geo?.country ?? loc?.country ?? rData?.loc?.country,
    currency_checkout: currency,
    day_of_week_local: daysOfWeek[today.getDay()],
    device_brand: ua?.device.vendor ?? rData?.ua?.device.vendor,
    device: ua?.device.model ?? rData?.ua?.device.model,
    facebook_click_id: new URLSearchParams(window.location.search).has("fbclid")
      ? new URLSearchParams(window.location.search).get("fbclid")?.split("#")[0]
      : null,
    google_analytics_id: google_analytics,
    google_click_id: new URLSearchParams(window.location.search).has("gclid")
      ? new URLSearchParams(window.location.search).get("gclid")?.split("#")[0]
      : null,
    hour_local: today.getHours(),
    internet_provider: loc?.isp ?? rData?.loc?.isp,
    ip: ip ?? rData?.ip,
    isBot: bot ?? rData?.bot,
    is_mobile_device: `${/iphone|ipod|android|ie|blackberry|fennec/.test(
      ua?.ua || rData?.ua?.ua.toLowerCase() || navigator.userAgent.toLowerCase()
    )}`,
    latitude: geo?.latitude ?? loc?.lat ?? rData?.loc?.lat,
    location: document.location.href || window.location.href,
    longitude: geo?.longitude ?? loc?.lon ?? rData?.loc?.lon,
    os: ua?.os.name ?? rData?.ua?.os.name,
    page_title: document.title,
    page_version: pageType,
    path: window.location.pathname,
    proxy: loc?.proxy ? true : false,
    url_host_name: window.location.hostname,
    url_main: cleanDomain(window.location.hostname),
    url_parameters: `${JSON.stringify(
      window.location.search
        .replace("?", "")
        .split("&")
        .map((param) => param.replace("=", ": "))
    )}`,
    user_is_bot: bot ? bot : false,
    region: geo?.region ?? loc?.regionName ?? rData?.loc?.regionName,
    referrer: document.referrer ? document.referrer : null,
    referrer_host_name: document.referrer
      ? new URL(document.referrer).hostname
      : null,
    release: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? null,
    screen_size: `${window.screen.width}x${window.screen.height}`,
    // Datalake split-test memberships keyed by PostHog flag name. Null
    // [VV12-TEARDOWN] lp-scrollytelling — at teardown this reverts to
    // `split_test: null` (with the "re-add a memberships branch per active
    // experiment" note), unless another experiment has claimed it by then.
    // The cookie only exists for visitors the middleware bucketed on the
    // rain-jacket LP homes, so its presence is the "in experiment" signal.
    // Note this tags every later beacon event for the full 30-day cookie
    // life, and includes visitors the compatibility gate diverted out of
    // PostHog arm-enrolment — so the datalake population is a superset of
    // the PostHog exposure population. Join on exposure when comparing.
    split_test: (() => {
      const memberships: Record<string, string> = {}
      const vv12 = Cookies.get(VV12_COOKIE)
      if (isVv12Variant(vv12)) {
        memberships[VV12_FEATURE_FLAG] = vv12
      }
      // [VV11-TEARDOWN] lp-no-discount-framing (DEV-10019) — the cookie only
      // exists for visitors the middleware resolved on the baerskin-hoodie-4
      // LP. Key matches the PostHog flag so datalake + PostHog join on one
      // name.
      const vv11 = Cookies.get(VV11_COOKIE)
      if (isVv11Variant(vv11)) {
        memberships[VV11_FEATURE_FLAG] = vv11
      }
      // [/VV11-TEARDOWN]
      // [VV9-TEARDOWN] all-paypal-express — the cookie only exists for
      // visitors middleware bucketed on an opted-in app (apps/store). Key
      // matches the PostHog flag so datalake + PostHog join on one name.
      const vv9 = Cookies.get(VV9_COOKIE)
      if (isVv9Variant(vv9)) {
        memberships[VV9_FEATURE_FLAG] = vv9
      }
      // [/VV9-TEARDOWN]
      return Object.keys(memberships).length ? memberships : null
    })(),
    // [/VV12-TEARDOWN]
    temperature: weather?.temp ?? rData?.w?.temp ?? null,
    timestamp: today.toISOString(),
    timezone: geo?.timezone ?? loc?.timezone ?? rData?.loc?.timezone,
    user_agent: ua?.ua || rData?.ua?.ua || navigator.userAgent,
    user_anonymous_id: gtmUpi,
    utm_campaign: `${
      new URLSearchParams(window.location.search).has("utm_campaign")
        ? new URLSearchParams(window.location.search)
            .get("utm_campaign")
            ?.split("#")[0]
        : null
    }`,
    utm_content: `${
      new URLSearchParams(window.location.search).has("utm_content")
        ? new URLSearchParams(window.location.search)
            .get("utm_content")
            ?.split("#")[0]
        : null
    }`,
    utm_medium: `${
      new URLSearchParams(window.location.search).has("utm_medium")
        ? new URLSearchParams(window.location.search)
            .get("utm_medium")
            ?.split("#")[0]
        : null
    }`,
    utm_source: `${
      new URLSearchParams(window.location.search).has("utm_source")
        ? new URLSearchParams(window.location.search)
            .get("utm_source")
            ?.split("#")[0]
        : null
    }`,
    utm_term: new URLSearchParams(window.location.search).has("utm_term")
      ? new URLSearchParams(window.location.search)
          .get("utm_term")
          ?.split("#")[0]
      : null,
    vibes: vibes ?? rData?.vibes,
    weather_description: weather?.main ?? rData?.w?.main ?? null,
    weather_description_specific:
      weather?.description ?? rData?.w?.description ?? null,
  }
}

declare global {
  interface Window {
    __devtools?: boolean
  }
}

export const trackAction = (
  name: string,
  data?: any,
  specific?: {
    exp?: boolean
    ga?: boolean
    posthog?: boolean
    datalake?: boolean
    vercel?: boolean
  }
) => {
  try {
    if (window.__devtools) {
      window.dispatchEvent(
        new CustomEvent("devtools:track", {
          detail: { fn: "trackAction", name, data: data ?? {} },
        })
      )
    }
  } catch {
    // devtools emission must never break tracking
  }

  const { vibes } = getTrackingCookies()
  const exp = specific?.hasOwnProperty("exp") ? specific.exp : true
  const ga = specific?.hasOwnProperty("ga") ? specific.ga : true
  const posthog = specific?.hasOwnProperty("posthog") ? specific.posthog : false
  const eventId = uuidv7()

  let debugMode = false

  if (isBrowser()) {
    const languageProps = getBrowserLanguageProps()

    if (
      window.location.search.includes("lobster=true") ||
      process.env.NODE_ENV === "development"
    ) {
      debugMode = true
    }

    // ── DataLayer bridges (must run BEFORE gtag so data is on the GTM
    //    data model when the gtag event fires) ──────────────────────────

    // purchase: push eventModel bridge to dataLayer.
    // Only include the `event` key when gtag is missing — otherwise the
    // subsequent dispatchGtag call fires the GTM event and adding `event`
    // here would cause double-firing of tags.
    if (name === "purchase") {
      const purchaseData = {
        ...languageProps,
        ...data,
        event_id: eventId,
        vibes,
      }
      const gtagAvailable = typeof window.gtag === "function"
      dispatchDataLayerPush({
        ...(!gtagAvailable && { event: "purchase" }),
        eventModel: purchaseData,
        purchase_eventModel: purchaseData,
      })
    }

    // ── SDK dispatches ────────────────────────────────────────────────

    // Google Analytics (direct best-effort dispatch)
    if (!specific || ga === true) {
      dispatchGtag(name, {
        ...languageProps,
        ...data,
        event_id: eventId,
        vibes,
      })
    }

    // Exponea / Bloomreach (direct best-effort dispatch)
    if (!specific || exp === true) {
      dispatchExponea(name, {
        ...languageProps,
        ...data,
        event_id: eventId,
        vibes: name !== "consent" ? vibes : undefined,
      })
    }

    // PostHog — readiness handled inside trackPostHog/withPostHogClient
    if (!specific || posthog === true) {
      trackPostHog(name, {
        ...languageProps,
        ...data,
        event_id: eventId,
        vibes,
      })
    }
  }

  if (!specific || exp === false || specific.datalake) {
    const endpointDomain = `${APP_URL}/api`
    const endpoint = `${endpointDomain}/t`
    const body = {
      channelId: window.channel,
      data: {
        ...data,
        eventId,
      },
      name,
      user: baseParametersForTracking(),
      vibes,
    }

    if (debugMode) {
      console.group("EVENT: " + name)
      console.log("data", data)
      console.groupCollapsed("metadata")
      console.log("user", baseParametersForTracking())
      console.log("vibes", vibes)
      console.groupEnd()
      console.groupEnd()
    }

    const encodedBody = objectToShiftedBase64(body, 24)
    const blob = new Blob([encodedBody], {
      type: "text/plain",
    })
    sendBacon(endpoint, blob)
  }

  // Vercel Analytics — only flat primitives allowed
  const vercel = specific?.hasOwnProperty("vercel") ? specific.vercel : true
  if (!specific || vercel) {
    const flatData: Record<string, string | number | boolean | null> = {}
    for (const [key, value] of Object.entries(data ?? {})) {
      if (
        value === null ||
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean"
      ) {
        flatData[key] = value
      }
    }
    track(name, flatData)
  }
}

interface TrackingBufferSlot {
  eventString: string
  timerId: NodeJS.Timeout | null
  pending: {
    eventString: string
    data: any
    specific?: Parameters<typeof trackAction>[2]
  } | null
}

export const useTracking = create<any>()((set, get) => ({
  buffers: {} as Record<string, TrackingBufferSlot>,
  /**
   * Sends any payloads still held back by an open coalescing window.
   * Wired to `pagehide` below so deferred events survive navigation.
   */
  flushBuffered: () => {
    const buffers: Record<string, TrackingBufferSlot> = get().buffers
    for (const [name, slot] of Object.entries(buffers)) {
      if (slot.timerId) {
        clearTimeout(slot.timerId)
      }
      if (slot.pending) {
        trackAction(name, slot.pending.data, slot.pending.specific)
      }
    }
    set({ buffers: {} })
  },
  /**
   * Coalesces bursts of the same event: the first call sends immediately,
   * further calls within `timeout` are held back and only the latest
   * distinct payload is sent when the window expires (one beacon per
   * window instead of one per call).
   */
  trackActionBuffered: (
    name: string,
    data?: any,
    specific?: Parameters<typeof trackAction>[2],
    timeout: number = 3000
  ) => {
    if (!name || !data) {
      console.warn("trackAction buffer: name or data missing")
      return
    }

    const buffers: Record<string, TrackingBufferSlot> = get().buffers
    const slot = buffers[name]
    const serialized = JSON.stringify(data)

    // Window open — coalesce: drop exact duplicates, defer distinct
    // payloads to the end of the window (latest wins)
    if (slot?.timerId) {
      const lastQueuedString = slot.pending?.eventString ?? slot.eventString
      if (lastQueuedString === serialized) {
        return
      }
      set({
        buffers: {
          ...buffers,
          [name]: {
            ...slot,
            pending: { eventString: serialized, data, specific },
          },
        },
      })
      return
    }

    // No open window — send immediately (leading edge) and open one
    trackAction(name, data, specific)

    const expireWindow = () => {
      const current: Record<string, TrackingBufferSlot> = get().buffers
      const currentSlot = current[name]
      if (currentSlot?.pending) {
        // Trailing send; keep the window rolling so a sustained stream
        // stays at one beacon per window
        const { pending } = currentSlot
        trackAction(name, pending.data, pending.specific)
        set({
          buffers: {
            ...get().buffers,
            [name]: {
              eventString: pending.eventString,
              timerId: setTimeout(expireWindow, timeout),
              pending: null,
            },
          },
        })
        return
      }
      // Window closed with nothing pending — drop the slot entirely so
      // the buffer map doesn't grow with every distinct event name
      const { [name]: _closed, ...remaining } = get().buffers as Record<
        string,
        TrackingBufferSlot
      >
      set({ buffers: remaining })
    }

    set({
      buffers: {
        ...buffers,
        [name]: {
          eventString: serialized,
          timerId: setTimeout(expireWindow, timeout),
          pending: null,
        },
      },
    })
  },
}))

// Deferred payloads must not be lost when the user navigates away —
// sendBeacon inside trackAction is safe to call during pagehide
if (isBrowser()) {
  window.addEventListener("pagehide", () => {
    useTracking.getState().flushBuffered()
  })
}
